import React from 'react';
import { 
 Input, 
 Select, 
 DatePicker, 
 Checkbox, 
 TextArea, 
 Upload,
 Button
} from '@/components/ui';
import { Eye, RefreshCw } from 'lucide-react';
import type { UserNewFormData } from '../types/userNew.types';

interface BasicInfoStepProps {
 formData: UserNewFormData;
 updateField: (field: keyof UserNewFormData, value: any) => void;
 generatePassword: () => void;
}

export function BasicInfoStep({ formData, updateField, generatePassword }: BasicInfoStepProps) {
 return (
 <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
 {/* Left Column - 2/3 width */}
 <div className="lg:col-span-2 space-y-8">
 {/* Section: Thông tin cá nhân */}
 <div className="bg-white rounded-md p-6 border border-slate-100">
 <h2 className="sm:text-sm text-xs text-slate-800 mb-6 flex items-center gap-2">
 Thông tin cá nhân
 </h2>
 
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 {/* Avatar Upload */}
 <div className="md:row-span-3">
 <label className="block sm:text-sm text-xs text-slate-500 mb-2 tracking-wider">
 Ảnh đại diện *
 </label>
 <div className="w-full h-48 border-2 border-dashed border-slate-100 rounded-md bg-slate-50/50 flex flex-col items-center justify-center p-4 hover:border-emerald-200 transition-colors cursor-pointer group">
 <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-slate-400 group-hover:text-emerald-600 transition-colors mb-3">
 <i className="ri-upload-cloud-2-line text-2xl" />
 </div>
 <p className="sm:text-sm text-xs text-slate-600 mb-1">Tải ảnh lên</p>
 <p className="sm:text-sm text-xs text-slate-400">PNG, JPG (tối đa 2MB)</p>
 </div>
 </div>

 <Input
 label="Họ và tên *"
 placeholder="Nhập họ và tên"
 value={formData.full_name}
 onChange={(e) => updateField('full_name', e.target.value)}
 />

 <Select
 label="Giới tính *"
 value={formData.gender}
 onValueChange={(val) => updateField('gender', val)}
 options={[
 { value: 'male', label: 'Nam' },
 { value: 'female', label: 'Nữ' },
 { value: 'other', label: 'Khác' },
 ]}
 />

 <DatePicker
 label="Ngày sinh *"
 value={formData.birthday}
 onChange={(val) => updateField('birthday', val)}
 />


 <Input
   label="Năm sinh"
   type="number"
   placeholder="Ví dụ: 1990"
   value={formData.birth_year?.toString() || ''}
   onChange={(e) => updateField('birth_year', e.target.value ? Number(e.target.value) : undefined)}
   />
 <Input
 label="CMND/CCCD *"
 placeholder="Nhập số CMND/CCCD"
 value={formData.id_number}
 onChange={(e) => updateField('id_number', e.target.value)}
 />

 <DatePicker
 label="Ngày cấp *"
 value={formData.id_issue_date}
 onChange={(val) => updateField('id_issue_date', val)}
 />

 <Input
 label="Nơi cấp"
 placeholder="Nhập nơi cấp CMND/CCCD"
 value={formData.id_issue_place}
 onChange={(e) => updateField('id_issue_place', e.target.value)}
 className="md:col-span-1"
 />

 <Select
 label="Quốc tịch"
 value={formData.nationality}
 onValueChange={(val) => updateField('nationality', val)}
 options={[
 { value: 'Việt Nam', label: 'Việt Nam' },
 { value: 'Khác', label: 'Khác' },
 ]}
 />

 <Select
 label="Tình trạng hôn nhân"
 value={formData.marital_status}
 onValueChange={(val) => updateField('marital_status', val)}
 options={[
 { value: 'single', label: 'Độc thân' },
 { value: 'married', label: 'Đã kết hôn' },
 { value: 'divorced', label: 'Ly hôn' },
 { value: 'widowed', label: 'Góa bụa' },
 ]}
 />

 <Input
 label="Số điện thoại *"
 placeholder="Nhập số điện thoại"
 value={formData.phone}
 onChange={(e) => updateField('phone', e.target.value)}
 />

 <Input
 label="Email *"
 placeholder="Nhập email"
 type="email"
 value={formData.email}
 onChange={(e) => updateField('email', e.target.value)}
 />

 <Input
 label="Địa chỉ thường trú"
 placeholder="Nhập địa chỉ thường trú"
 value={formData.permanent_address}
 onChange={(e) => updateField('permanent_address', e.target.value)}
 className="md:col-span-2"
 />

 <Input
 label="Địa chỉ hiện tại"
 placeholder="Nhập địa chỉ hiện tại"
 value={formData.current_address}
 onChange={(e) => updateField('current_address', e.target.value)}
 className="md:col-span-2"
 />
 </div>
 </div>

 {/* Section: Thông tin tài khoản */}
 <div className="bg-white rounded-md p-6 border border-slate-100">
 <h2 className="sm:text-sm text-xs text-slate-800 mb-6 flex items-center gap-2">
 Thông tin tài khoản
 </h2>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <Input
 label="Tên đăng nhập *"
 placeholder="Nhập tên đăng nhập"
 value={formData.username}
 onChange={(e) => updateField('username', e.target.value)}
 />

 <div className="space-y-2">
 <label className="block sm:text-sm text-xs text-slate-500 tracking-wider mb-1.5">
 Mật khẩu tạm thời *
 </label>
 <div className="flex gap-2">
 <div className="relative flex-1">
 <input
 type="password"
 className="w-full pl-3 pr-10 py-2.5 rounded-md border border-slate-100 bg-slate-50/50 sm:text-sm text-xs focus:ring-1 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all outline-none"
 value={formData.temporary_password}
 onChange={(e) => updateField('temporary_password', e.target.value)}
 />
 <Button 
 variant="ghost" 
 size="icon" 
 className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 text-slate-400 hover:text-slate-600"
 >
 <Eye className="w-4 h-4" />
 </Button>
 </div>
 <Button
 variant="outline"
 onClick={generatePassword}
 className="px-3 border-slate-100 sm:text-sm text-xs text-slate-600 hover:bg-slate-50 flex items-center gap-2 transition-all"
 >
 <RefreshCw className="w-3.5 h-3.5" />
 <span>Tạo ngẫu nhiên</span>
 </Button>
 </div>
 </div>

 <Input
 label="Email đăng nhập"
 placeholder="Nhập email đăng nhập (nếu khác)"
 value={formData.account_email}
 onChange={(e) => updateField('account_email', e.target.value)}
 />

 <div className="md:col-span-2 mt-2">
 <div className="flex items-center gap-3 bg-slate-50/50 p-3 rounded-md border border-slate-100/50">
 <Checkbox
 checked={formData.require_password_change}
 onCheckedChange={(val) => updateField('require_password_change', val)}
 />
 <span className="sm:text-sm text-xs text-slate-600">
 Yêu cầu đổi mật khẩu khi đăng nhập lần đầu
 </span>
 </div>
 </div>
 </div>
 </div>
 </div>

 {/* Right Column - 1/3 width */}
 <div className="lg:col-span-1 space-y-6">
 <div className="bg-white rounded-md p-6 border border-slate-100 h-full">
 <h2 className="sm:text-sm text-xs text-slate-800 mb-6 flex items-center gap-2">
 Thông tin bổ sung
 </h2>

 <div className="space-y-6">
 <DatePicker
 label="Ngày vào công ty *"
 value={formData.join_date}
 onChange={(val) => updateField('join_date', val)}
 />

 <Select
 label="Loại nhân sự *"
 value={formData.employment_type}
 onValueChange={(val) => updateField('employment_type', val)}
 options={[
 { value: 'full_time', label: 'Chính thức' },
 { value: 'part_time', label: 'Bán thời gian' },
 { value: 'contract', label: 'Hợp đồng' },
 { value: 'probation', label: 'Thử việc' },
 ]}
 />

 <Select
 label="Trạng thái *"
 value={formData.status}
 onValueChange={(val) => updateField('status', val)}
 options={[
 { value: 'active', label: 'Đang làm việc' },
 { value: 'on_leave', label: 'Nghỉ phép' },
 { value: 'suspended', label: 'Tạm nghỉ' },
 { value: 'resigned', label: 'Đã nghỉ việc' },
 ]}
 />

 <Select
 label="Nguồn tuyển dụng"
 value={formData.recruitment_source}
 onValueChange={(val) => updateField('recruitment_source', val)}
 options={[
 { value: 'referral', label: 'Giới thiệu' },
 { value: 'linkedin', label: 'LinkedIn' },
 { value: 'website', label: 'Website' },
 { value: 'other', label: 'Khác' },
 ]}
 />

 <TextArea
 label="Ghi chú"
 placeholder="Nhập ghi chú (nếu có)"
 value={formData.notes}
 onChange={(e) => updateField('notes', e.target.value)}
 rows={5}
 />
 </div>
 </div>
 </div>
 </div>
 );
}
